import {DUST_DEVIL_SOUND_ID} from './biomeEffects/dustDevil';
import {SNOW_SQUALL_SOUND_ID} from './biomeEffects/snowDrift';
import {FIELD_FLIGHT_SOUNDS} from './fieldAmbienceSounds';
import {GROUND_BACKGROUNDS} from './groundBackgrounds';

/**
 * ЗВУКИ ЭФФЕКТОВ ПОДЛОЖЕК: какой эффект поля чем звучит и на какой подложке
 * (сами эффекты — configs/biomeEffects, играет звуки scenes/FieldEffectSounds).
 *
 * Строка перечня — ПРИВЯЗКА: дорожка эффекта (id) и ее звуки по подложкам.
 * Привязки бывают двух видов (kind):
 *
 *  - «буря» (STORM): звук и картинка идут ТОЛЬКО ВМЕСТЕ. Пока звук не загружен,
 *    эффект на поле не начинается; звук звучит все время, пока идет эффект, —
 *    в кадре он у игрока или нет: слышимость плавно идет за силой эффекта (она
 *    же ведет плотность картинки) и скачком не меняется. Так звучат вьюга и
 *    пыльный вихрь. Звук у бури один, и в перечне отложенных звуков он помечен
 *    как тот, которого ждут (isAwaited);
 *
 *  - «порыв» (GUST): звук эффекта не держит — как голоса птиц не держат птиц:
 *    не загружен — порыв пройдет молча. Но, в отличие от птиц, жребия нет:
 *    когда порыв ПОПАДАЕТ В КАДР игрока, всегда играет один случайный из его
 *    звуков. Пока порыв в кадре, звук звучит; порыв ушел из кадра или кончился
 *    — звук плавно затихает и обрывается; доигравший сам звук для того же
 *    появления заново не запускается. Так звучат порывы поземки, песчаные
 *    змейки, порыв ветра над лугом, вуали ветра и пар гейзеров.
 *
 * «В кадре» — то же, что у голосов птиц (см. configs/fieldAmbienceSounds и
 * utils/effects/flightView): то, что показывает камера, без закрытого панелями
 * интерфейса и с отступом от краев. Насколько группа частиц в кадре, считает
 * контроллер эффекта (scenes/FieldBiomeEffects: inViewAlpha).
 *
 * ЗВУКИ — ПО ПОДЛОЖКАМ, ЯВНО (backgrounds: подложка -> имена отложенных
 * звуков из configs/deferredSounds). Подложки, которой в строке нет, привязка
 * не касается, даже если на ней идет тот же эффект. Одна привязка может
 * озвучивать и дорожки разных эффектов с общей меткой: морской бриз острова
 * собран тем же построителем, что порыв ветра над лугом, и шелестит той же
 * травой (так решил владелец игры). Громкость каждого звука и подложки,
 * на которых его файл качается, — в перечне отложенных звуков; подложки там и
 * здесь обязаны сходиться (это сверяет тест).
 *
 * Метки дорожек и расчет силы эффекта живут у построителей эффектов
 * (utils/effects/biome): с привязками их сводит сцена (scenes/FieldEffectSounds),
 * здесь — только имена и числа.
 */

/** Виды привязок */
export const FIELD_EFFECT_SOUND_KINDS = Object.freeze({STORM: 'storm', GUST: 'gust'});

/** Привязки: что на поле звучит */
export const FIELD_EFFECT_SOUND_IDS = Object.freeze({
  // Снежный заряд вьюги над зимним полем
  SNOW_SQUALL: 'snowSquall',
  // Пыльный вихрь красного каньона
  DUST_DEVIL: 'dustDevil',
  // Порыв поземки (одиночные ленты между порывами молчат)
  SNOW_DRIFT_GUST: 'snowDriftGust',
  // Песчаные змейки пустынь
  SAND_STREAMERS: 'sandStreamers',
  // Порыв ветра над лугом: травяные волны и листья
  WIND_GUST: 'windGust',
  // Вуали ветра: соляная пыль, пыль над лугами и плато
  WIND_VEILS: 'windVeils',
  // Извержение нарисованного гейзера (у края поля и вокруг него)
  GEYSER_ERUPTION: 'geyserEruption',
  // Струйка пара из трещины на самом поле
  GEYSER_FUMAROLE: 'geyserFumarole',
  // Светящийся лабиринт спиралей
  LABYRINTH_GLOW: 'labyrinthGlow',
});

const {STORM, GUST} = FIELD_EFFECT_SOUND_KINDS;
const IDS = FIELD_EFFECT_SOUND_IDS;

// Числа «бури»
const STORM_SOUND = Object.freeze({
  kind: STORM,
  // Быстрее, чем за столько, слышимость не проходит от тишины до полной и
  // обратно, мс: даже если картинку убрали разом, звук стихает плавно
  fadeMs: 900,
});

// Числа «порыва»; у привязки любое из них может быть своим
const GUST_SOUND = Object.freeze({
  kind: GUST,
  // Порыв «в кадре», когда хотя бы одна его частица в кадре игрока не
  // прозрачнее этого: порыв, который только проступает или уже тает, еще (уже)
  // не слышен. Мерка та же, что у птиц
  minInViewAlpha: FIELD_FLIGHT_SOUNDS.minVisibleAlpha,
  // Звук начинается неслышным и быстро нарастает, мс: порыв тоже входит в
  // кадр не разом
  fadeInMs: 300,
  // Порыв ушел из кадра или кончился, а звук еще звучит: за это время он
  // затихает, мс. Вернулся в кадр, пока звук не затих, — слышимость
  // возвращается; пробыл вне кадра дольше — возвращение считается новым
  // появлением и озвучивается заново. Дольше, чем у крика птиц (полторы
  // секунды): владелец игры услышал, что шум ветра затухает резко, — ровному
  // шуму нужно больше времени, чтобы стихнуть незаметно
  fadeOutMs: 2500,
  // Слышимость звука — его доля (0..1) в такой степени. Ухо слышит громкость
  // не по линейке: при ровном (линейном) спаде шум сначала будто не меняется,
  // а под конец обрывается. Квадрат дает спад, ровный на слух: на середине
  // затихания звук уже вчетверо тише, а последняя треть времени — еле слышный
  // хвост. Нарастает звук по той же кривой — мягко, без толчка
  levelPower: 2,
  // Наименьшая пауза между началами звуков привязки, мс; 0 — паузы нет: звучит
  // каждое появление (но звуков привязки разом — не больше maxVoices)
  minPauseMs: 0,
  // Сколько звуков привязки звучит разом. Один: появление, начавшееся под
  // чужой звук, проходит молча — два шума ветра внахлест слились бы в гул
  maxVoices: 1,
});

const WINTER = Object.freeze([
  GROUND_BACKGROUNDS.WINTER_1, GROUND_BACKGROUNDS.WINTER_2, GROUND_BACKGROUNDS.WINTER_3,
  GROUND_BACKGROUNDS.WINTER_4, GROUND_BACKGROUNDS.WINTER_5,
]);

// Свист ветра в поземке, шелест ветра в траве, «волшебный» ветер солончака,
// шипение пара
const SNOW_WINDS = Object.freeze(['wind_snow_1', 'wind_snow_2', 'wind_snow_3']);
const GRASS_WINDS = Object.freeze(['wind_grass_1', 'wind_grass_2']);
const MAGIC_WINDS = Object.freeze(['wind_magic_1', 'wind_magic_2', 'wind_magic_3']);
const STEAM_HISSES = Object.freeze(['steam_1', 'steam_2', 'steam_3', 'steam_4']);

/** Одни и те же звуки — нескольким подложкам */
const assignSounds = (groundBackgroundIds, soundIds) => Object.fromEntries(
  groundBackgroundIds.map((groundBackgroundId) => [groundBackgroundId, soundIds])
);

// Пар гейзеров шипит коротко (записи — полторы-две с половиной секунды):
// полторы секунды затихания были бы почти всем звуком
const STEAM_SOUND = Object.freeze({...GUST_SOUND, fadeOutMs: 600});

export const FIELD_EFFECT_SOUNDS = Object.freeze([
  // Вой вьюги: заряд ждет его и звучит с ним в лад
  Object.freeze({
    ...STORM_SOUND,
    id: IDS.SNOW_SQUALL,
    backgrounds: Object.freeze(assignSounds(WINTER, Object.freeze([SNOW_SQUALL_SOUND_ID]))),
  }),
  // Ветер песчаной бури: правила те же, что у воя вьюги
  Object.freeze({
    ...STORM_SOUND,
    id: IDS.DUST_DEVIL,
    backgrounds: Object.freeze({[GROUND_BACKGROUNDS.RED_1]: Object.freeze([DUST_DEVIL_SOUND_ID])}),
  }),
  Object.freeze({
    ...GUST_SOUND,
    id: IDS.SNOW_DRIFT_GUST,
    backgrounds: Object.freeze(assignSounds(WINTER, SNOW_WINDS)),
  }),
  Object.freeze({
    ...GUST_SOUND,
    id: IDS.SAND_STREAMERS,
    // Ленты песка светятся (сложение цветов) и потому бледны: плотность их
    // штриха — четверть единицы даже в разгар порыва, до общей мерки (0.3) она
    // не доходит никогда. Своя мерка — около половины этой плотности
    minInViewAlpha: 0.12,
    backgrounds: Object.freeze(assignSounds(
      [GROUND_BACKGROUNDS.DESERT_1, GROUND_BACKGROUNDS.DESERT_2], Object.freeze(['wind_sand_1'])
    )),
  }),
  // Два луга с травяными волнами и остров: его морской бриз — тот же порыв
  // (та же травяная волна, без листьев), и звук ему владелец игры назначил тот же
  Object.freeze({
    ...GUST_SOUND,
    id: IDS.WIND_GUST,
    backgrounds: Object.freeze(assignSounds(
      [GROUND_BACKGROUNDS.GREEN_1, GROUND_BACKGROUNDS.GREEN_2, GROUND_BACKGROUNDS.ISLAND_1], GRASS_WINDS
    )),
  }),
  // У вуалей звук свой у каждой подложки: на соли — «волшебный» ветер, на
  // лугах и плато — тот же шелест травы, что у порыва ветра над лугом
  Object.freeze({
    ...GUST_SOUND,
    id: IDS.WIND_VEILS,
    backgrounds: Object.freeze({
      [GROUND_BACKGROUNDS.SALT_1]: MAGIC_WINDS,
      ...assignSounds(
        [GROUND_BACKGROUNDS.GREEN_3, GROUND_BACKGROUNDS.GREEN_4, GROUND_BACKGROUNDS.PLATO_1], GRASS_WINDS
      ),
    }),
  }),
  // Извержение шипит каждый раз, когда оно в кадре. Группа извержения живет
  // дольше струи (доживают оторвавшиеся облака пара): шипит она, только пока
  // струя бьет не слабее такой доли своей силы
  Object.freeze({
    ...STEAM_SOUND,
    id: IDS.GEYSER_ERUPTION,
    minStrength: 0.3,
    backgrounds: Object.freeze({[GROUND_BACKGROUNDS.GEYSER_1]: STEAM_HISSES}),
  }),
  // Струйка из трещины шипит каждый раз, когда она в кадре (так решил владелец
  // игры: сначала шипела одна из нескольких, с паузой в пять секунд). Чтобы
  // шипение не стояло стеной, реже стали сами струйки: новая — не раньше чем
  // через три секунды после прежней (configs/biomeEffects/geyserSteam), а самая
  // долгая запись шипения — две с половиной: прежний звук к ней уже отзвучал
  // Шипений разом — до двух, как и самих струек: струйка, вошедшая в кадр не
  // сразу (поднялась у его кромки), иначе попадала бы под шипение соседней и
  // проходила молча
  Object.freeze({
    ...STEAM_SOUND,
    id: IDS.GEYSER_FUMAROLE,
    maxVoices: 2,
    backgrounds: Object.freeze({[GROUND_BACKGROUNDS.GEYSER_1]: STEAM_HISSES}),
  }),
  // Лабиринты спиралей светятся под тот же «волшебный» ветер, что и вуали соли
  // (так решил владелец игры): звук — когда проснувшийся лабиринт в кадре игрока
  Object.freeze({
    ...GUST_SOUND,
    id: IDS.LABYRINTH_GLOW,
    backgrounds: Object.freeze({[GROUND_BACKGROUNDS.SPIRAL_1]: MAGIC_WINDS}),
  }),
]);

const NO_SOUNDS = Object.freeze([]);

// Привязки по подложкам: у каждой — ее звуки на этой подложке вместо всей таблицы
const SOUNDS_BY_GROUND_BACKGROUND = Object.freeze(
  FIELD_EFFECT_SOUNDS.reduce((byBackground, {backgrounds, ...binding}) => {
    Object.keys(backgrounds).forEach((groundBackgroundId) => {
      byBackground[groundBackgroundId] = Object.freeze([
        ...(byBackground[groundBackgroundId] || []),
        Object.freeze({...binding, soundIds: backgrounds[groundBackgroundId]}),
      ]);
    });
    return byBackground;
  }, {})
);

/**
 * Звуки эффектов подложки поля.
 * @param {string|null} [groundBackgroundId] — идентификатор подложки
 * @returns {Array<Object>} привязки этой подложки: {id, kind, soundIds — имена
 *   отложенных звуков, и числа своего вида}; пусто — на подложке ничего не звучит
 */
export const getFieldEffectSounds = (groundBackgroundId) => (
  // Только свои ключи: подложка приходит и из файла состояния, а имя вроде
  // «constructor» нашлось бы в прототипе объекта
  Object.prototype.hasOwnProperty.call(SOUNDS_BY_GROUND_BACKGROUND, groundBackgroundId)
    ? SOUNDS_BY_GROUND_BACKGROUND[groundBackgroundId]
    : NO_SOUNDS
);
